import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ArrowLeft, Mail, Send } from "lucide-react";
import { Link } from "react-router-dom";

export default function TestEmail() {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    from: "vini.apoena@unitedstrategists.org",
    to: "felipe.afonsobarbosa@gmail.com",
    subject: "Hello from Postmark",
    htmlBody: "<strong>Hello</strong> dear Postmark user.",
    textBody: "Hello from Postmark!",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("send-email", {
        body: formData,
      });

      if (error) throw error;

      console.log("Email response:", data);
      toast.success(`Email sent successfully! Message ID: ${data.messageId}`);
    } catch (error: any) {
      console.error("Error sending email:", error);
      toast.error(error.message || "Failed to send email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-8">
      <header className="sticky top-0 z-10 bg-background border-b border-border px-4 py-3 flex items-center gap-3">
        <Link to="/">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <Mail className="h-5 w-5 text-primary" />
        <h1 className="text-lg font-semibold">Test Postmark Email</h1>
      </header>

      <div className="px-4 py-6 max-w-2xl mx-auto">
        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="from">From</Label>
              <Input
                id="from"
                type="email"
                value={formData.from}
                onChange={(e) => setFormData({ ...formData, from: e.target.value })}
                required
                placeholder="sender@example.com"
              />
              <p className="text-xs text-muted-foreground">
                Must be a verified sender signature in Postmark
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="to">To</Label>
              <Input
                id="to"
                type="email"
                value={formData.to}
                onChange={(e) => setFormData({ ...formData, to: e.target.value })}
                required
                placeholder="recipient@example.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                required
                placeholder="Email subject"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="textBody">Text Body</Label>
              <Textarea
                id="textBody"
                value={formData.textBody}
                onChange={(e) => setFormData({ ...formData, textBody: e.target.value })}
                placeholder="Plain text version of the email"
                rows={4}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="htmlBody">HTML Body</Label>
              <Textarea
                id="htmlBody"
                value={formData.htmlBody}
                onChange={(e) => setFormData({ ...formData, htmlBody: e.target.value })}
                placeholder="<strong>HTML</strong> version of the email"
                rows={6}
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading ? (
                "Sending..."
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Test Email
                </>
              )}
            </Button>
          </form>
        </Card>

        <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <h3 className="font-semibold mb-2 text-sm">Important Notes:</h3>
          <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
            <li>The "From" address must be verified in your Postmark account</li>
            <li>Check Postmark dashboard for delivery status</li>
            <li>Check spam folder if email doesn't arrive</li>
            <li>View edge function logs for debugging</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
